import React, {useEffect, useState} from "react";
import PlotlyPlot from "../../../../common/components/PlotlyPlot";
import {loadProtocolBiomarkerData, loadProtocolBiomarkerOptions, loadProtocolBiomarkerNames} from "../../services/protocols";
import {PlotParams} from "react-plotly.js";
import {IProtocol} from "../../../types/protocol";
import {ICompany} from "../../../../common/types";
import {BiomarkerColumnNames} from "../../types/biomarkerNames";
import {ChartTypes} from "../charts";
import {Box, Loader, Text, Select} from "@mantine/core";

interface BiomarkerDistributionChartProps {
    activeCompany: ICompany;
    selectedProtocol: IProtocol | undefined;
    refreshTrigger?: number
}

export const BiomarkerDistributionChart: React.FC<BiomarkerDistributionChartProps> = ({activeCompany, selectedProtocol, refreshTrigger}) => {
    const [biomarkerDistributionData, setBiomarkerDistributionData] = useState<PlotParams>()
    const [loading, setLoading] = useState<boolean>(false)
    const [selectedBiomarker, setSelectedBiomarker] = useState<string>(null)
    const [frequencyBand, setFrequencyBand] = useState<string | null>(null)
    const [channelName, setChannelName] = useState<string | null>(null)
    const [freqOptions, setFreqOptions] = useState<{ value: string; label: string }[]>([]);
    const [channelOptions, setChannelOptions] = useState<{ value: string; label: string }[]>([]);
    const [biomarkerOptions, setBiomarkerOptions] = useState<{ value: string; label: string }[]>([]);
    const [parameterRefreshTick, setParameterRefreshTick] = useState<number>(0)

    const column_names: BiomarkerColumnNames[] = [
        BiomarkerColumnNames.PRE_STIM_VALUES,
        BiomarkerColumnNames.STIM_VALUES,
        BiomarkerColumnNames.POST_STIM_VALUES,
        BiomarkerColumnNames.PRE_TO_STIM_P_VALUE,
        BiomarkerColumnNames.STIM_TO_POST_P_VALUE,
        BiomarkerColumnNames.PRE_TO_POST_P_VALUE,
    ]
    const require_all_columns: boolean = false

    useEffect(() => {
        async function initializeBiomarkerOptions() {
            if (selectedProtocol) {
                setLoading(true);
                try {
                    const biomarkerResponse = await loadProtocolBiomarkerNames(activeCompany.id, selectedProtocol, column_names, require_all_columns);
                    const biomarker_names = biomarkerResponse.data.map((biomarker) => ({ value: biomarker, label: biomarker }));
                    setBiomarkerOptions(biomarker_names);
                    if (biomarker_names.length > 0) {
                        setSelectedBiomarker(biomarker_names[0].value);
                    }
                } catch (error) {
                    console.error("Failed to load biomarker options", error);
                } finally {
                    setLoading(false);
                }
            }
        }
        initializeBiomarkerOptions();
    }, [selectedProtocol, refreshTrigger]);

    useEffect(() => {
        async function updateDropdownOptions() {
            if (selectedProtocol && selectedBiomarker) {
                // load frequency band options
                const frequencyBands = await loadProtocolBiomarkerOptions(activeCompany.id, selectedProtocol, selectedBiomarker, BiomarkerColumnNames.FREQUENCY_BAND);

                // load channel name options
                const channelNames = await loadProtocolBiomarkerOptions(activeCompany.id, selectedProtocol, selectedBiomarker, BiomarkerColumnNames.CHANNEL_NAME);

                // Convert string lists to mappings for dropdown options
                // Filter out null or undefined or empty string values to avoid invalid dropdown options
                const isValidValue = (val: any): boolean => {
                    return val !== null &&
                           val !== undefined &&
                           val !== "" &&
                           typeof val === 'string';
                };
                const freqOptionsMapped = (frequencyBands?.data && Array.isArray(frequencyBands.data)) ? frequencyBands.data.filter(isValidValue).map((band) => ({ value: band, label: band })) : [];
                const channelOptionsMapped = (channelNames?.data && Array.isArray(channelNames.data)) ? channelNames.data.filter(isValidValue).map((channel) => ({ value: channel, label: channel })) : [];

                setFreqOptions(freqOptionsMapped);
                setChannelOptions(channelOptionsMapped);
                if (freqOptionsMapped.length > 0) {
                        setFrequencyBand(freqOptionsMapped[0].value);
                    } else {
                        setFrequencyBand(null); // Reset to null if no options available
                    }
                if (channelOptionsMapped.length > 0) {
                        setChannelName(channelOptionsMapped[0].value);
                    } else {
                        setChannelName(null); // Reset to null if no options available
                    }
                setParameterRefreshTick((t) => t + 1)
            }
        }
        updateDropdownOptions();
    }, [selectedBiomarker]);

    useEffect(() => {
        if (selectedProtocol && selectedBiomarker) {
            setLoading(true)
            loadProtocolBiomarkerData(activeCompany.id, selectedProtocol, ChartTypes.BIOMARKER_DISTRIBUTION, selectedBiomarker, column_names, frequencyBand, channelName).then((data: any) => {
                // data.data expected to be array of [stim_condition, pre_stim_values, stim_values, post_stim_values]

                /*
                 * Dynamic Box Plot Centering Algorithm:
                 *
                 * This implementation centers box plots above their x-axis labels based on which
                 * time periods have data. For example:
                 * - If a stim has only "pre" data: box is centered at position 0
                 * - If a stim has "stim" and "post" data: boxes are at -0.1625 and +0.1625 (centered)
                 * - If a stim has all three: boxes are at -0.325, 0, and +0.325
                 *
                 * This ensures that boxes are always visually centered above their labels regardless
                 * of which combination of time periods have data.
                 */

                // Build a structured map of data by stim condition
                type StimData = {
                    pre: number[];
                    stim: number[];
                    post: number[];
                    preToStimPValue: number | null;
                    stimToPostPValue: number | null;
                    preToPostPValue: number | null;
                };

                const isFiniteNumber = (value: unknown): value is number => typeof value === "number" && Number.isFinite(value);
                const mergePValue = (current: number | null, incoming: unknown): number | null => {
                    if (!isFiniteNumber(incoming)) {
                        return current;
                    }
                    if (current === null) {
                        return incoming;
                    }
                    // Duplicated rows can repeat p-values; keep the most conservative observed value.
                    return Math.min(current, incoming);
                };

                const getSignificanceLabel = (pValue: number): string => {
                    if (pValue < 0.0001) return "****";
                    if (pValue < 0.001) return "***";
                    if (pValue < 0.01) return "**";
                    if (pValue < 0.05) return "*";
                    return "ns";
                };

                const stimDataMap = new Map<string, StimData>();
                const stimConditions: string[] = [];

                (data.data || []).forEach(([
                    stim,
                    pre_values,
                    stim_values,
                    post_values,
                    pre_to_stim_p_value,
                    stim_to_post_p_value,
                    pre_to_post_p_value,
                ]: [
                    string,
                    Array<number>,
                    Array<number>,
                    Array<number>,
                    number | null,
                    number | null,
                    number | null,
                ]) => {
                    if (!stimDataMap.has(stim)) {
                        stimDataMap.set(stim, {
                            pre: [],
                            stim: [],
                            post: [],
                            preToStimPValue: null,
                            stimToPostPValue: null,
                            preToPostPValue: null,
                        });
                        stimConditions.push(stim);
                    }
                    const stimData = stimDataMap.get(stim)!;
                    stimData.pre.push(...(pre_values || []));
                    stimData.stim.push(...(stim_values || []));
                    stimData.post.push(...(post_values || []));
                    stimData.preToStimPValue = mergePValue(stimData.preToStimPValue, pre_to_stim_p_value);
                    stimData.stimToPostPValue = mergePValue(stimData.stimToPostPValue, stim_to_post_p_value);
                    stimData.preToPostPValue = mergePValue(stimData.preToPostPValue, pre_to_post_p_value);
                });

                // Calculate dynamic x-positions for each box to center them
                const pre_x_positions: number[] = [];
                const pre_y_values: number[] = [];
                const stim_x_positions: number[] = [];
                const stim_y_values: number[] = [];
                const post_x_positions: number[] = [];
                const post_y_values: number[] = [];

                type StimCenters = {
                    pre: number | null;
                    stim: number | null;
                    post: number | null;
                };
                const stimCentersMap = new Map<string, StimCenters>();

                stimConditions.forEach((stimCondition, stimIndex) => {
                    const stimData = stimDataMap.get(stimCondition)!;

                    // Determine which time periods have data
                    const hasPre = stimData.pre.length > 0;
                    const hasStim = stimData.stim.length > 0;
                    const hasPost = stimData.post.length > 0;

                    // Count how many boxes we'll display for this stim
                    const boxCount = (hasPre ? 1 : 0) + (hasStim ? 1 : 0) + (hasPost ? 1 : 0);

                    // Calculate centered offsets based on box count
                    let offsets: number[];
                    if (boxCount === 1) {
                        offsets = [0];
                    } else if (boxCount === 2) {
                        offsets = [-0.1625, 0.1625];
                    } else { // boxCount === 3
                        offsets = [-0.325, 0, 0.325];
                    }

                    // Assign offsets to the time periods that have data
                    let offsetIndex = 0;
                    const preOffset = hasPre ? offsets[offsetIndex++] : null;
                    const stimOffset = hasStim ? offsets[offsetIndex++] : null;
                    const postOffset = hasPost ? offsets[offsetIndex++] : null;

                    stimCentersMap.set(stimCondition, {
                        pre: preOffset !== null ? stimIndex + preOffset : null,
                        stim: stimOffset !== null ? stimIndex + stimOffset : null,
                        post: postOffset !== null ? stimIndex + postOffset : null,
                    });

                    // Add data points with calculated x-positions
                    if (hasPre && preOffset !== null) {
                        stimData.pre.forEach(value => {
                            pre_x_positions.push(stimIndex + preOffset);
                            pre_y_values.push(value);
                        });
                    }

                    if (hasStim && stimOffset !== null) {
                        stimData.stim.forEach(value => {
                            stim_x_positions.push(stimIndex + stimOffset);
                            stim_y_values.push(value);
                        });
                    }

                    if (hasPost && postOffset !== null) {
                        stimData.post.forEach(value => {
                            post_x_positions.push(stimIndex + postOffset);
                            post_y_values.push(value);
                        });
                    }
                });

                // Build traces, filtering out empty ones
                const traces: any[] = [];

                if (pre_y_values.length > 0) {
                    traces.push({
                        ...data.figure.data[0],
                        x: pre_x_positions,
                        y: pre_y_values,
                    });
                }

                if (stim_y_values.length > 0) {
                    traces.push({
                        ...data.figure.data[1],
                        x: stim_x_positions,
                        y: stim_y_values,
                    });
                }

                if (post_y_values.length > 0) {
                    traces.push({
                        ...data.figure.data[2],
                        x: post_x_positions,
                        y: post_y_values,
                    });
                }

                const allYValues = [...pre_y_values, ...stim_y_values, ...post_y_values];
                const annotations: any[] = [];
                const shapes: any[] = [];
                let useBracketBand = false;

                if (allYValues.length > 0) {
                    // Use paper coordinates for consistent visual sizing.
                    const baseYPaper = 0.85;
                    const bracketStepPaper = 0.075;
                    const bracketHeightPaper = 0.03;
                    const labelOffsetPaper = 0.075;

                    stimConditions.forEach((stimCondition) => {
                        const stimData = stimDataMap.get(stimCondition)!;
                        const centers = stimCentersMap.get(stimCondition)!;
                        const baseY = baseYPaper;
                        const upperY = baseYPaper + bracketStepPaper;
                        const hasPreToStim = centers.pre !== null && centers.stim !== null && stimData.preToStimPValue !== null;
                        const hasStimToPost = centers.stim !== null && centers.post !== null && stimData.stimToPostPValue !== null;
                        const shouldSplitAtStim = hasPreToStim && hasStimToPost;
                        const stimBoundaryOffset = shouldSplitAtStim ? 0.01 : 0;
                        const comparisons = [
                            { key: "preToStim", x1: centers.pre, x2: centers.stim, pValue: stimData.preToStimPValue, y: baseY },
                            { key: "stimToPost", x1: centers.stim, x2: centers.post, pValue: stimData.stimToPostPValue, y: baseY },
                            { x1: centers.pre, x2: centers.post, pValue: stimData.preToPostPValue, y: upperY },
                        ];

                        comparisons.forEach(({key, x1, x2, pValue, y}) => {
                            if (x1 === null || x2 === null || pValue === null) {
                                return;
                            }

                            let left = Math.min(x1, x2);
                            let right = Math.max(x1, x2);

                            // When both adjacent comparisons exist, split them slightly at the shared stim edge.
                            if (shouldSplitAtStim && key === "preToStim") {
                                right = Math.max(left, right - stimBoundaryOffset);
                            }
                            if (shouldSplitAtStim && key === "stimToPost") {
                                left = Math.min(right, left + stimBoundaryOffset);
                            }

                            useBracketBand = true;
                            shapes.push(
                                { type: "line", xref: "x", yref: "paper", x0: left, x1: right, y0: y, y1: y, line: { color: "#4A4A4A", width: 1.5 } },
                                { type: "line", xref: "x", yref: "paper", x0: left, x1: left, y0: y - bracketHeightPaper, y1: y, line: { color: "#4A4A4A", width: 1.5 } },
                                { type: "line", xref: "x", yref: "paper", x0: right, x1: right, y0: y - bracketHeightPaper, y1: y, line: { color: "#4A4A4A", width: 1.5 } },
                            );

                            annotations.push({
                                x: (left + right) / 2,
                                y: y + labelOffsetPaper,
                                xref: "x",
                                yref: "paper",
                                text: getSignificanceLabel(pValue),
                                showarrow: false,
                                font: { size: 12, color: "#2A2A2A" },
                            });
                        });
                    });
                }

                setBiomarkerDistributionData({
                    layout: {
                        ...data.figure.layout,
                        xaxis: {
                            ...data.figure.layout.xaxis,
                            type: 'linear',
                            tickmode: 'array',
                            tickvals: stimConditions.map((_, index) => index),
                            ticktext: stimConditions,
                            zeroline: false,
                        },
                        yaxis: {
                            ...data.figure.layout.yaxis,
                            // Reserve a top in-plot band for significance brackets/labels.
                            ...(useBracketBand ? { domain: [0, 0.80] } : {}),
                        },
                        boxmode: 'overlay',
                        shapes: [...(data.figure.layout.shapes || []), ...shapes],
                        annotations: [...(data.figure.layout.annotations || []), ...annotations],
                    },
                    config: { responsive: true },
                    data: traces
                })
            }).catch(() => {
                // eat the error since notification would already be called
            }).finally(() => {
                setLoading(false)
            })
        }
    }, [selectedProtocol, parameterRefreshTick])

    const renderLoading = () => {
        return (
            <Box style={{position: "absolute", zIndex: "10", top: "calc(50% - 2em)", left: "calc(50% - 2em)"}}>
                <Loader size="4em" color="var(--bios-strong-accent)"/>
            </Box>
        )
    }

    return (
        <Box style={{height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden'}}>
            <Box style={{flex: '0 0 auto', display: 'flex', gap: '1rem', padding: '0.5rem', alignItems: 'center'}}>
                <Box style={{display: 'flex', flexDirection: 'column', gap: '0.25rem'}}>
                    <Text size="xs" color="dimmed">Biomarker</Text>
                    <Select
                        data={biomarkerOptions}
                        value={selectedBiomarker}
                        onChange={(v) => setSelectedBiomarker(v || null)}
                        style={{width: 180}}
                        disabled={biomarkerOptions.length === 0}
                    />
                </Box>
                <Box style={{display: 'flex', flexDirection: 'column', gap: '0.25rem'}}>
                    <Text size="xs" color="dimmed">Frequency band</Text>
                    <Select
                        data={freqOptions}
                        value={frequencyBand}
                        onChange={(v) => {
                            setFrequencyBand(v || null)
                            setParameterRefreshTick((t) => t + 1)
                            }}
                        style={{width: 180}}
                        disabled={freqOptions.length === 0}
                    />
                </Box>
                <Box style={{display: 'flex', flexDirection: 'column', gap: '0.25rem'}}>
                    <Text size="xs" color="dimmed">Channel Name</Text>
                    <Select
                        data={channelOptions}
                        value={channelName}
                        onChange={(v) => {
                            setChannelName(v || null)
                            setParameterRefreshTick((t) => t + 1)
                            }}
                        style={{width: 180}}
                        disabled={channelOptions.length === 0}
                    />
                </Box>
            </Box>

            <Box style={{flex: '1 1 auto', minHeight: 0, position: 'relative', overflow: 'hidden'}}>
                {biomarkerDistributionData ? (
                    <Box style={{height: '100%', width: '100%'}}>
                        <PlotlyPlot {...biomarkerDistributionData}/>
                        {loading ? renderLoading() : null}
                    </Box>
                ) : (
                    <Box style={{height: '100%', position: 'relative'}}>
                        {renderLoading()}
                    </Box>
                )}
            </Box>
        </Box>
    )
}
